/**
 * Interlineado para CKEditor 5, escrito a medida.
 *
 * ⚠️ POR QUÉ NO SE USA EL OFICIAL: `@ckeditor/ckeditor5-line-height` es una función
 * PREMIUM de CKEditor. Con la licencia gratuita ('GPL') el editor no arranca y lanza
 * `license-key-feature-not-allowed {"featureName":"LineHeight"}`. Los plugins propios
 * no están sujetos a esa restricción, solo los suyos.
 *
 * Cómo funciona: añade un atributo de bloque `lineHeight` a los párrafos y encabezados,
 * que se guarda en el HTML como `style="line-height: X"`. Al leer, cualquier
 * `line-height` en línea se convierte de vuelta al atributo, de modo que el contenido
 * que ya existía se sigue entendiendo.
 */
/**
 * ⚠️ UIModel, NO Model. El paquete `ckeditor5` reexporta con `export *` tanto el
 * `Model` del MOTOR DE DOCUMENTOS (@ckeditor/ckeditor5-engine) como el modelo de
 * interfaz, que desde la v48 se llama `UIModel` (con `ViewModel` como alias).
 * `import { Model }` devuelve el del motor: al construir con él las opciones del
 * desplegable, `label` y `withText` se ignoran y la lista sale VACÍA, sin ningún
 * error en consola. Es exactamente el fallo que tuvo esta primera versión.
 */
import { Plugin, Command, UIModel, createDropdown, addListToDropdown, Collection } from 'ckeditor5';

/** Bloques a los que se les puede aplicar. */
const ELEMENTOS = ['paragraph', 'heading1', 'heading2', 'heading3'];

/** Valor que significa "sin interlineado propio": se hereda del CSS del sitio. */
export const DEFECTO = 'default';

class LineHeightCommand extends Command {
    refresh() {
        const bloque = first(this.editor.model.document.selection.getSelectedBlocks());

        this.isEnabled = !!bloque && this.editor.model.schema.checkAttribute(bloque, 'lineHeight');
        this.value = bloque ? bloque.getAttribute('lineHeight') || DEFECTO : DEFECTO;
    }

    execute(options = {}) {
        const model = this.editor.model;
        const valor = options.value;

        model.change(writer => {
            for (const bloque of model.document.selection.getSelectedBlocks()) {
                if (!model.schema.checkAttribute(bloque, 'lineHeight')) {
                    continue;
                }

                // 'default' quita el atributo en vez de escribir un valor: así el HTML
                // no se llena de estilos redundantes y manda el CSS del sitio.
                if (!valor || valor === DEFECTO) {
                    writer.removeAttribute('lineHeight', bloque);
                } else {
                    writer.setAttribute('lineHeight', valor, bloque);
                }
            }
        });
    }
}

function first(iterable) {
    for (const item of iterable) {
        return item;
    }
    return null;
}

export default class LineHeight extends Plugin {
    static get pluginName() {
        return 'LineHeight';
    }

    init() {
        const editor = this.editor;
        const opciones = editor.config.get('lineHeight.options') || [DEFECTO, 1, 1.15, 1.5, 2];

        // --- Esquema: el atributo solo vale en los bloques de texto ---
        for (const elemento of ELEMENTOS) {
            if (editor.model.schema.isRegistered(elemento)) {
                editor.model.schema.extend(elemento, { allowAttributes: 'lineHeight' });
            }
        }

        // --- Modelo -> HTML ---
        editor.conversion.for('downcast').attributeToAttribute({
            model: 'lineHeight',
            view: valor => ({ key: 'style', value: { 'line-height': String(valor) } }),
        });

        // --- HTML -> modelo. Sin esto, al reabrir un registro se perdería el ajuste ---
        editor.conversion.for('upcast').attributeToAttribute({
            view: { styles: { 'line-height': /.*/ } },
            model: {
                key: 'lineHeight',
                value: viewElement => viewElement.getStyle('line-height'),
            },
        });

        editor.commands.add('lineHeight', new LineHeightCommand(editor));

        // --- Desplegable de la barra ---
        editor.ui.componentFactory.add('lineHeight', locale => {
            const comando = editor.commands.get('lineHeight');
            const dropdown = createDropdown(locale);
            const items = new Collection();

            for (const opcion of opciones) {
                const model = new UIModel({
                    commandParam: opcion,
                    label: opcion === DEFECTO ? 'Por defecto' : String(opcion),
                    withText: true,
                });

                model.bind('isOn').to(comando, 'value', valor => String(valor) === String(opcion));

                items.add({ type: 'button', model });
            }

            addListToDropdown(dropdown, items);

            dropdown.buttonView.set({
                tooltip: 'Interlineado',
                withText: true,
            });

            // El botón muestra el valor del párrafo donde está el cursor, para que se
            // vea de un vistazo qué interlineado tiene sin abrir el desplegable.
            dropdown.buttonView.bind('label').to(comando, 'value', valor =>
                !valor || valor === DEFECTO ? 'Interlineado' : 'Interlineado: ' + valor
            );

            dropdown.bind('isEnabled').to(comando);

            dropdown.on('execute', evt => {
                editor.execute('lineHeight', { value: evt.source.commandParam });
                editor.editing.view.focus();
            });

            return dropdown;
        });
    }
}
